import * as global from '../../../services/global.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Company } from '../models/company';
import { Soleproprietor } from '../models/soleproprietor';
import { Individual } from '../models/individual';
import { Capability } from '../interfaces/capability';

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {

  private static ONBOARDING_PATH = global.API_URL + '/api/onboarding';
  private httpOptions: object;
  private httpHeaders: HttpHeaders;
  private httpParams: HttpParams;

  constructor(private http: HttpClient) {
    this.httpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
  }

  /**
   * Parses the capabilities received from an API and returns them as an array of Capability objects.
   * Filters out capabilities that do not meet configuration requirements such as being enabled,
   * requested, and allowed, or not having a valid verification status.
   *
   * @param {any} capabilitiesFromAPI - The raw data containing capabilities information retrieved from an API.
   * @return {Capability[]} An array of Capability objects. If parsing fails, an empty array is returned.
   */
  public parseCapabilities(capabilitiesFromAPI: any): Capability[]  {
    const capabilities: Capability[] = [];

    try {
      const capabilitiesData = capabilitiesFromAPI;

      Object.keys(capabilitiesData).forEach(key => {
        const capability = capabilitiesData[key];
        if (!capability.isOK ||
            !capability.details.enabled ||
            !capability.details.requested ||
            !capability.details.allowed ||
            capability.details.verificationStatus !== 'valid') {
          console.warn(`OnboardingService.parseCapabilities: Capability ${key} is not properly configured:`, capability);
        }

        capabilities.push(capability);
      });
    } catch (error) {
      console.error('Failed to parse capabilities:', error);
      return [];
    }

    return capabilities;
  }

  /**
   * Crea una persona (api-panel)
   * @param individual Individual
   * @returns Observable
   */
  public createIndividual(individual: Individual) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.post(OnboardingService.ONBOARDING_PATH + '/legaldata-solepropietor-persons', individual, this.httpOptions);
  }

  /**
   * Crea un autónomo (api-panel)
   * @param soleproprietorship Soleproprietor
   * @returns Observable
  */
  public createSoleproprietorship(soleproprietorship: Soleproprietor) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.post(OnboardingService.ONBOARDING_PATH + '/legaldata-solepropietors', soleproprietorship, this.httpOptions);
  }

  /**
   * Crea una compañía (api-panel)
   * @param company Company
   * @returns Observable
   */
  public createCompany(company: Company) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.post(OnboardingService.ONBOARDING_PATH + '/legaldata-company', company, this.httpOptions);
  }

  /**
   * Actualiza una persona (api-panel)
   * @param clienteId number
   * @param individual Individual
   * @returns Observable
   */
  public updateIndividual(clienteId: number, individual: Individual) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.put(OnboardingService.ONBOARDING_PATH + '/legaldata-solepropietor-persons/' + clienteId, individual, this.httpOptions);
  }

  /**
   * Actualiza una soleproprietorship (api-panel)
   * @param clienteId number
   * @param soleproprietorship Soleproprietor
   * @returns Observable
   */
  public updateSoleproprietorship(clienteId: number, soleproprietorship: Soleproprietor) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    // tslint:disable-next-line:max-line-length
    return this.http.put(OnboardingService.ONBOARDING_PATH + '/legaldata-solepropietors/' + clienteId, soleproprietorship, this.httpOptions);
  }

  /**
   * Actualiza una compañía (api-panel)
   * @param clienteId number
   * @param company Company
   * @returns Observable
   */
  public updateCompany(clienteId: number, company: Company) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.put(OnboardingService.ONBOARDING_PATH + '/legaldata-company/' + clienteId, company, this.httpOptions);
  }

  /**
   * Obtiene una persona (api-panel)
   * @param clienteId
   * @returns Observable
   */
  public getIndividual(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.get(OnboardingService.ONBOARDING_PATH + '/legaldata-solepropietor-persons/' + clienteId, this.httpOptions);
  }

  /**
   * Obtiene datos de un autónomo (api-panel)
   * @param clienteId
   * @returns Observable
   */
  public getSoleProprietorship(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.get(OnboardingService.ONBOARDING_PATH + '/legaldata-solepropietors/' + clienteId, this.httpOptions);
  }

  /**
   * Obtiene datos de una compañía (api-panel)
   * @param clienteId
   * @returns Observable
   */
  public getCompany(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.get(OnboardingService.ONBOARDING_PATH + '/legaldata-company/' + clienteId, this.httpOptions);
  }

  /**
   * Registra los datos de una persona en Adyen (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public adyenIndividual(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.post(OnboardingService.ONBOARDING_PATH + '/merchant/new/person/by-customer-id/' + clienteId, this.httpOptions);
  }

  /**
   * Actualiza los datos de una persona en Adyen (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public adyenUpdateIndividual(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.patch(OnboardingService.ONBOARDING_PATH + '/merchant/update/person/by-customer-id/' + clienteId, this.httpOptions);
  }

  /**
   * Registra los datos de un autónomo en Adyen (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public adyenSoleProprietorship(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    // tslint:disable-next-line:max-line-length
    return this.http.post(OnboardingService.ONBOARDING_PATH + '/merchant/new/sole-proprietorship/by-customer-id/' + clienteId, this.httpOptions);
  }

  /**
   * Actualiza los datos de un autónomo en Adyen (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public adyenUpdateSoleProprietorship(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.patch(
      OnboardingService.ONBOARDING_PATH + '/merchant/update/sole-proprietorship/by-customer-id/' + clienteId,
      this.httpOptions
    );
  }

  /**
   * Registra los datos de una compañía en Adyen (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public adyenCompany(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.post(OnboardingService.ONBOARDING_PATH + '/merchant/new/company/by-customer-id/' + clienteId, this.httpOptions);
  }

  /**
   * Actualiza los datos de una compañía en Adyen (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public adyenUpdateCompany(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.patch(OnboardingService.ONBOARDING_PATH + '/merchant/update/company/by-customer-id/' + clienteId, this.httpOptions);
  }

  /**
   * Comprueba los datos de una persona (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public checkAdyenIndividual(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.get(OnboardingService.ONBOARDING_PATH + '/merchant/check/person/by-customer-id/' + clienteId, this.httpOptions);
  }

  /**
   * Comprueba los datos de un autónomo (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public checkAdyenSoleProprietorship(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.get(
      OnboardingService.ONBOARDING_PATH + '/merchant/check/sole-proprietorship/by-customer-id/' + clienteId,
      this.httpOptions
    );
  }

  /**
   * Comprueba los datos de una compañía (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public checkAdyenCompany(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.get(OnboardingService.ONBOARDING_PATH + '/merchant/check/company/by-customer-id/' + clienteId, this.httpOptions);
  }

  /**
   * Modifica el tipo legal de un cliente [autónomo(1) || empresa(0)] (api-panel)
   * @param clienteId number
   * @param legalEntityData object
   * @returns Observable
   */
  public setLegalEntity(clienteId: number, legalEntityData: object) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };

    return this.http.put(global.API_URL + '/api/customers/' + clienteId, legalEntityData, this.httpOptions);
  }

  /**
   * Enviar la petición para obtener el account holder (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public finish(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };
    return this.http.post(OnboardingService.ONBOARDING_PATH + '/merchant/finish/by-customer-id/' + clienteId, this.httpOptions);
  }

  /**
   * Enviar la petición para comprobar que tiene account holder (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public checkFinish(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };
    return this.http.get(OnboardingService.ONBOARDING_PATH + '/merchant/check/finish/by-customer-id/' + clienteId, this.httpOptions);
  }

  /**
   * Enviar la petición para obtener la URL de Hosted de Adyen y entregar la documentación (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public getHostedUrl(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };
    return this.http.get(OnboardingService.ONBOARDING_PATH + '/merchant/hosted-onboard-url/by-customer-id/' + clienteId, this.httpOptions);
  }

  /**
   * Enviar la petición para comprobar que tiene pasado el KYC (Adyen)
   * @param clienteId
   * @returns Observable
   */
  public checkKyc(clienteId: number) {
    this.httpOptions = {
      headers: this.httpHeaders,
      params: {}
    };
    return this.http.get(OnboardingService.ONBOARDING_PATH + '/merchant/check/kyc/by-customer-id/' + clienteId, this.httpOptions);
  }

}
